import { getAuthenticatedResponse } from "@/lib/http-client";

/**
 * File name from a Content-Disposition header. Prefers the RFC 5987 `filename*=UTF-8''…` form —
 * the only one that carries a non-ASCII (e.g. Arabic) name intact — and falls back to plain
 * `filename=`, which is what the CSV/XLSX/ZIP exports send.
 */
export function filenameFromDisposition(
  disposition: string | null,
): string | undefined {
  if (!disposition) return undefined;

  const extended = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1];
  if (extended) {
    try {
      return decodeURIComponent(extended.trim());
    } catch {
      /* malformed escape — fall through to the plain form */
    }
  }

  return disposition.match(/filename\s*=\s*"?([^";]+)"?/i)?.[1];
}

/** Download a file (CSV, XLSX, ZIP) from an authenticated export endpoint. */
export async function downloadAuthenticatedFile(
  path: string,
  params?: object,
  filename = "export",
): Promise<void> {
  const response = await getAuthenticatedResponse(path, params);

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const resolvedName =
    filenameFromDisposition(response.headers.get("content-disposition")) ??
    filename;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = resolvedName;
  anchor.click();
  URL.revokeObjectURL(url);
}
